import {
    FrontendFilter,
    FrontendFilterGroup, FrontendFilterInputMultiple,
    FrontendFilterInputSelectOne,
    FrontendFilterInputText,
    FrontendFilterOption,
    FrontendFilterResultColumn,
    FrontendFilterValues,
    FrontendFilterInputDate,
    FrontendFilterInputNumber,
    FrontendFilterResultRowModifierCssClasses, FrontendFilterInputBooleanSelect, FrontendFilterInputSelectOneWithSearch, FrontendFilterBatchAction, FrontendFilterResultRowModifier
} from "@/components/FrontendFilters/structs";
import store from "@/store"
import DateHelper from "date-helper-util";
import Vue from "vue";
import {i18n} from "@/main"
import {hasAllPermissions, hasLocation, hasPermission, isFeatureEnabled} from "@/helpers/permissions";
import _ from "lodash";
import {price} from "@/helpers/numbers";
import axios from "axios";
import moment from "moment/moment";
import {downloadBase64url, getDataURL} from "@/helpers/download";
import {dispatchRequestStatus} from "@/helpers/dispatch";

// Definieer deze arrays als return via een functie zodat de translations zijn ingeladen op het moment van aanroepen
const FULFILMENT_OPTIONS: () => FrontendFilterOption[] = () => [
    new FrontendFilterOption('pickup', i18n.t(`frontend-filters.orders.filters.fulfilment_method.options.pickup`).toString()),
    new FrontendFilterOption('delivery', i18n.t(`frontend-filters.orders.filters.fulfilment_method.options.delivery`).toString()),
]

const COUNTRY_OPTIONS: () => FrontendFilterOption[] = () =>
    store.getters.getFrontendParameterValue('order-search', 'searchable_countries')
        .map((dropdownValue: any) => new FrontendFilterOption(dropdownValue.value, dropdownValue.label))

const VALIDATION_STATUS_OPTIONS: () => FrontendFilterOption[] = () => [
    new FrontendFilterOption('on-hold', i18n.t(`frontend-filters.orders.filters.validation_status.options.on-hold`).toString()),
    new FrontendFilterOption('released', i18n.t(`frontend-filters.orders.filters.validation_status.options.released`).toString()),
]

const DEFAULT_MAX_AGE_DAYS = 14

const defaultFrontendFilterInputParams = {
    disabled: (filter: FrontendFilter, filterValues: FrontendFilterValues): boolean => {
        const excludeFieldsFromDisable = ['dispatcher_type', 'dispatcher_identifier', 'responsible_location_type', 'responsible_location_identifier']

        if (!hasPermission('canmanagealllocations') && excludeFieldsFromDisable.includes(filter.identifier)) {
            return false
        }

        return !!filterValues.order_identifier?.length // Disable all inputs when searching for order identifier

    },
};

export const getCarrierMethodOptions = () => _.chain(store.state.carrierInformation)
    .map((camase: Record<string,any>) => camase.active_methods)
    .flatten()
    .uniqBy('id')
    .filter((method: Record<string,any>) => method.pivot.is_active)
    .map((method: Record<string,any>) => new FrontendFilterOption(
        method.id.toString(),
        `${method.carrier.display_name} ${String.fromCharCode(8594)} ${method.display_name}`,
    ))
    .orderBy('text')
    .value()

export class FrontendFiltersOrderSearch implements FrontendFilterGroup {
    identifier = 'orders';
    subIdentifier = 'general';

    pageTitleLanguageKey = `frontend-filters.page-titles.orders`;
    pageIcon = 'pe-7s-search';

    searchApiPath = `/api/rest/sales-orders-search`;

    searchApiDefaultParameters = (filterValues: FrontendFilterValues) => ({
        include: [
            'sales_order_lines',
            'current_dispatch_requests_with_paused.storage_location.storage_zone',
            'addresses',
            'current_sales_order_payments',
            'current_refund_requests',
            'global_sales_order_status',
            'comment_count',
            ...(filterValues.include_rerouted_replaced_dispatch_requests
                ? ['has_rerouted_dispatch_request']
                : []
            ),
        ].join(','),
    })

    resultsPerPage = 25;

    resultRowPreprocessor = (resultRow: Record<string, any>): Record<string, any> => {
        // Because we only show data of the 'first' active DRQ of the order, we sort the DRQs so that specific types come up
        resultRow.current_dispatch_requests_with_paused = _.sortBy(
            resultRow.current_dispatch_requests_with_paused,
            drq => {
                if (drq.dispatch_action === 'crossdocking') return 0
                if (drq.status === 'cancelled') return 2;
                return 1;
            }
        )

        return resultRow
    };

    resultRowModifiers = [
        () => new FrontendFilterResultRowModifier(
            (resultRow) => resultRow.current_dispatch_requests_with_paused[0]?.status === 'cancelled',
            (store.getters.getFrontendParameterValue('order-search', 'cancelled-orders.strikethrough')) ? FrontendFilterResultRowModifierCssClasses.strikethrough : undefined,
            (store.getters.getFrontendParameterValue('order-search', 'cancelled-orders.show-icon')) ? 'ban' : undefined,
            'text-danger',
            'frontend-filters.orders.result-row-modifiers.tooltips.cancelled',
        ),
        () => new FrontendFilterResultRowModifier(
            // De 'fallback-orders' feature is momenteel alleen voor Expert enabled dus vandaar de hardcoded 'fallback' dispatcher identifier
            (resultRow) => isFeatureEnabled('fallback-orders') && resultRow.current_dispatch_requests_with_paused[0]?.dispatcher_identifier === 'fallback',
            undefined,
            (store.getters.getFrontendParameterValue('order-search', 'fallback-orders.show-icon')) ? 'triangle-exclamation' : undefined,
            'text-warning',
            'frontend-filters.orders.result-row-modifiers.tooltips.fallback-order',
        ),
        () => new FrontendFilterResultRowModifier(
            (resultRow) => resultRow.validation_status === 'on-hold',
            undefined,
            (store.getters.getFrontendParameterValue('order-search', 'fallback-orders.show-icon')) ? 'triangle-exclamation' : undefined,
            'text-warning',
            'frontend-filters.orders.result-row-modifiers.tooltips.hold-order',
        ),
        () => new FrontendFilterResultRowModifier(
            (resultRow, filterValues) => {
                if (
                    filterValues.include_rerouted_replaced_dispatch_requests !== "1"
                    || resultRow.current_dispatch_requests_with_paused.length === 0
                    || hasPermission('canmanagealllocations')
                ) {
                    return false;
                }

                return resultRow.has_rerouted_dispatch_request
            },
            (store.getters.getFrontendParameterValue('order-search', 'dealer-changes.strikethrough')) ? FrontendFilterResultRowModifierCssClasses.strikethrough : undefined,
            (store.getters.getFrontendParameterValue('order-search', 'dealer-changes.show-icon')) ? 'route' : undefined,
            'text-normal',
            'frontend-filters.orders.result-row-modifiers.tooltips.rerouted',
        ),
        () => new FrontendFilterResultRowModifier(
            (resultRow) => !!resultRow.current_dispatch_requests_with_paused?.some((drq: Record<string,any>) => !!drq.paused_at),
            undefined,
            (store.getters.getFrontendParameterValue('order-search', 'paused-orders.show-icon')) ? 'circle-pause' : undefined,
            'text-warning',
            'frontend-filters.orders.result-row-modifiers.tooltips.paused-orders',
            !store.getters.getFrontendParameterValue('order-search', 'paused-orders.disable-for-batch-actions'),
        ),
    ]

    /**
     * @param result
     * @param filterValues
     */
    getResultLink(result: Record<string,string>, filterValues: FrontendFilterValues): Record<string,any> {
        return {
            name: 'OrderDetailOverview',
            params: {
                salesOrderIdentifier: result.order_identifier,
            },
            hash: (filterValues.include_rerouted_replaced_dispatch_requests === "1")
                ? '#include_rerouted_replaced_dispatch_requests=1'
                : ''
        }
    };

    batchActions = [
        new FrontendFilterBatchAction(
            'get-packing-slips',
            () => hasPermission('orders.dispatchrequests.downloadpackingslip'),
            async (selectedResults: Record<string,any>[]): Promise<boolean | void> => {
                if (selectedResults.length > store.getters.getFrontendParameterValue('order-search', 'batch_labels_warning_threshold')) {
                    if (!confirm(`LET OP! Weet je zeker dat je ${selectedResults.length} pakbonnen wilt downloaden?`)) {
                        return;
                    }
                }

                return axios.get(`/api/rest/dispatch-requests/batch-packing-slips`, {
                    params: {
                        identifiers: selectedResults.map((order: Record<string,any>) => order.current_dispatch_requests_with_paused[0].identifier).join(',')
                    },
                })
                    .then(response => {
                        const identifier = moment().unix()

                        if (response.data.errors.length) {
                            Vue.prototype.$alert(`De volgende fouten zijn opgetreden:<br /><br />* ${response.data.errors.join('<br />* ')}`, 'warning', false)
                        }
                        if (response.data.packing_slips_content_base64) {
                            downloadBase64url(getDataURL(response.data.packing_slips_content_type, response.data.packing_slips_content_base64), `Packingslip-batch-${identifier}.pdf`)
                        }
                        if (response.data.labels_content_base64) {
                            downloadBase64url(getDataURL(response.data.labels_content_type, response.data.labels_content_base64), `Labels-batch-${identifier}.pdf`)
                        }
                    })
            }
        ),
        new FrontendFilterBatchAction(
            'print-packing-slips',
            () => hasPermission('orders.dispatchrequests.printlabels') && !hasPermission('canmanagealllocations'),
            async (selectedResults: Record<string,any>): Promise<boolean | void> => {
                if (selectedResults.length > store.getters.getFrontendParameterValue('order-search', 'batch_labels_warning_threshold')) {
                    if (!confirm(`LET OP! Weet je zeker dat je ${selectedResults.length} labels wilt printen?`)) {
                        return;
                    }
                }

                return axios.get(`/api/rest/dispatch-requests/batch-packing-slips`, {
                    params: {
                        identifiers: selectedResults.map((order: Record<string,any>) => order.current_dispatch_requests_with_paused[0].identifier).join(','),
                        auto_print_label: 1,
                        auto_print_packing_slip: 0,
                    },
                })
                    .then(response => {
                        Vue.prototype.$alert(`De printopdracht is verstuurd`, 'info')

                        if (response.data.errors.length) {
                            Vue.prototype.$alert(`De volgende fouten zijn opgetreden:<br /><br />* ${response.data.errors.join('<br />* ')}`, 'warning', false)
                        }
                    })
            }
        ),
        new FrontendFilterBatchAction(
            'print-packing-slips-dummy',
            () => hasPermission('orders.dispatchrequests.printlabels') && hasPermission('canmanagealllocations'),
            async (selectedResults: Record<string,any>): Promise<boolean | void> => {
                Vue.prototype.$alert("Deze knop doet niks omdat je de de permissie `canmanagealllocations` hebt", 'info')
            }
        ),
    ];

    filters = [
        new FrontendFilter(
            'order_identifier',
            new FrontendFilterInputText({
                ...defaultFrontendFilterInputParams,
                placeholder: () => 'Bijv. 123456',
                disabled: () => false, // Override the default, otherwise it becomes disabled after inputting 1 character
            }),
        ),

        new FrontendFilter(
            'fulfilment_method',
            new FrontendFilterInputSelectOneWithSearch({...defaultFrontendFilterInputParams}),
            () => Promise.resolve(FULFILMENT_OPTIONS()),
        ),

        new FrontendFilter(
            'current_fulfilment_method',
            new FrontendFilterInputSelectOneWithSearch({...defaultFrontendFilterInputParams}),
            () => Promise.resolve(FULFILMENT_OPTIONS()),
        ),

        new FrontendFilter(
            'dispatch_type',
            new FrontendFilterInputSelectOneWithSearch({...defaultFrontendFilterInputParams}),
            () => store.getters.getDispatchTypes
                .then(() => Object.values(store.state.dispatchTypes ?? {})
                    // @ts-ignore
                    ?.filter((dispatchType: Record<string,any>): Boolean => dispatchType.is_active)
                    // @ts-ignore
                    ?.map((dispatchType: Record<string,any>) => new FrontendFilterOption(dispatchType.identifier, dispatchType.display_name))
                ),
            (options: FrontendFilterOption[], filterValues: FrontendFilterValues): Promise<FrontendFilterOption[]> => {
                if (!filterValues['dispatcher_type'] || !filterValues['dispatcher_identifier']) {
                    return Promise.resolve(options);
                }

                return store.dispatch('loadDispatchInfoForLocation', {type: filterValues['dispatcher_type'], identifier: filterValues['dispatcher_identifier']})
                    .then((dispatchTypes: Record<string,any>[]) =>
                        options.filter((option: FrontendFilterOption) =>
                            dispatchTypes.some((dispatchType: Record<string,any>) =>
                                dispatchType.identifier === option.value && dispatchType.is_active && dispatchType.is_active_for_location
                            )
                        )
                    ) as Promise<FrontendFilterOption[]>
            },
        ),

        new FrontendFilter(
            'dispatcher_type',
            new FrontendFilterInputSelectOneWithSearch({...defaultFrontendFilterInputParams}),
            () => store.getters.getLocationsForUser
                .then(() => _.chain(store.state.locationsForUser)
                    .map((location: Record<any,any>) => new FrontendFilterOption(location.location_type?.identifier, location.location_type?.display_name))
                    .uniqBy((option: FrontendFilterOption) => option.value)
                    .value()
                ),
        ),

        new FrontendFilter(
            'dispatcher_identifier',
            new FrontendFilterInputSelectOneWithSearch({...defaultFrontendFilterInputParams}),
            () => store.getters.getLocationsForUser
                .then(() => store.state.locationsForUser
                    .filter((location: Record<any,any>) => store.getters.getFrontendParameterValue('order-search', 'searchable_inactive_locations') || !!parseInt(location.active))
                    .map((location: Record<any,any>) => new FrontendFilterOption(
                        location.identifier,
                        `${location.display_name} (${location.identifier})`,
                        {location_type: location.location_type.identifier}
                    ))
            ),
            (options: FrontendFilterOption[], filterValues: FrontendFilterValues): FrontendFilterOption[] => {
                return options.filter((option: FrontendFilterOption) => {
                    if (!filterValues['dispatcher_type']) {
                        return true
                    }
                    return option.extra['location_type'] === filterValues['dispatcher_type']
                })
            },
        ),

        new FrontendFilter(
            'dispatch_request_status',
            new FrontendFilterInputSelectOneWithSearch({...defaultFrontendFilterInputParams}),
            () => store.getters.getConfig
                .then(() =>
                    store.getters.getFrontendParameterValue('order-search', 'searchable_dispatch_request_statuses')
                        .map((status: string) => {
                            return new FrontendFilterOption(status, dispatchRequestStatus(status))
                        })
                ),
        ),

        new FrontendFilter(
            'validation_status',
            new FrontendFilterInputSelectOneWithSearch({...defaultFrontendFilterInputParams}),
            () => Promise.resolve(VALIDATION_STATUS_OPTIONS()),
        ),

        new FrontendFilter(
            'responsible_location_identifier',
            new FrontendFilterInputSelectOneWithSearch({...defaultFrontendFilterInputParams}),
            () => store.getters.getLocationsForUser
                .then(() => store.state.locationsForUser
                    .filter((location: Record<any,any>) => location.location_type.identifier === 'store'
                        && (store.getters.getFrontendParameterValue('order-search', 'searchable_inactive_locations') || !!parseInt(location.active))
                    )
                    .map((location: Record<any,any>) => new FrontendFilterOption(
                        location.identifier,
                        `${location.display_name} (${location.identifier})`,
                        {location_type: location.location_type.identifier}
                    ))
                ),
            undefined,
            (filterValues: FrontendFilterValues): FrontendFilterValues => {
                filterValues['responsible_location_type'] = (filterValues['responsible_location_identifier']) ? 'store' : null
                return filterValues
            },
        ),

        new FrontendFilter(
            'sales_formula_identifier',
            new FrontendFilterInputSelectOneWithSearch({...defaultFrontendFilterInputParams}),
            () => store.getters.getConfig
                // @ts-ignore
                .then(() => Object.values(store.state.config['sales-formulas'] ?? []).map((value: string) => new FrontendFilterOption(value, value))),
        ),

        new FrontendFilter(
            'sales_channel_identifier',
            new FrontendFilterInputSelectOneWithSearch({...defaultFrontendFilterInputParams}),
            () => store.getters.getConfig
                // @ts-ignore
                .then(() => Object.values(store.state.config['sales-channels'] ?? []).map((value: string) => new FrontendFilterOption(value, value))),
        ),

        new FrontendFilter(
            'carrier_method',
            new FrontendFilterInputSelectOneWithSearch({...defaultFrontendFilterInputParams}),
            () => store.getters.getCarrierInformation
                .then(getCarrierMethodOptions),
        ),

        new FrontendFilter(
            'product_identifiers',
            new FrontendFilterInputText(defaultFrontendFilterInputParams), // Ondersteunt tijdelijk maar 1 product ivm fulltext-search problemen bij meerdere
            undefined,
            undefined,
            (filterValues: FrontendFilterValues): FrontendFilterValues => {
                const normalizedFilterValue = _.cloneDeep(filterValues)

                if (normalizedFilterValue['product_identifiers']) {
                    normalizedFilterValue['product_identifiers'] = [normalizedFilterValue['product_identifiers']]
                }

                return normalizedFilterValue
            },
        ),

        new FrontendFilter(
            'payment_method',
            new FrontendFilterInputSelectOneWithSearch({...defaultFrontendFilterInputParams}),
            () => store.getters.getPaymentMethodsInformation
                .then(() => {
                    const options: FrontendFilterOption[] = []
                    store.state.paymentMethodsInformation.forEach((provider: Record<string, any>) => {
                        provider.accounts.forEach((account: Record<string, any>) => {
                            account.methods.forEach((method: Record<string, any>) => {
                                options.push(new FrontendFilterOption(
                                    method.id.toString(),
                                    method.display_name,
                                ))
                            })
                        })
                    })
                    return options
                })
        ),

        new FrontendFilter(
            'has_comments',
            new FrontendFilterInputBooleanSelect({...defaultFrontendFilterInputParams}),
        ),
        new FrontendFilter(
            'is_paused',
            new FrontendFilterInputBooleanSelect({...defaultFrontendFilterInputParams}),
        ),

        new FrontendFilter(
            'refunds',
            new FrontendFilterInputSelectOneWithSearch({...defaultFrontendFilterInputParams}),
            () => Promise.resolve([ // Dit moet in een promise omdat anders i18n nog niet initialized is. De promise wordt nml pas at runtime aangeroepen
                new FrontendFilterOption('all', i18n.t(`frontend-filters.orders.filters.refunds.options.all`).toString()),
                new FrontendFilterOption('manual_processing', i18n.t(`frontend-filters.orders.filters.refunds.options.manual_processing`).toString()),
                new FrontendFilterOption('automatic_processing_delay', i18n.t(`frontend-filters.orders.filters.refunds.options.automatic_processing_delay`).toString()),
                new FrontendFilterOption('requested', i18n.t(`frontend-filters.orders.filters.refunds.options.requested`).toString()),
            ])
        ),

        new FrontendFilter(
            'has_assign_error',
            new FrontendFilterInputSelectOneWithSearch({...defaultFrontendFilterInputParams}),
            () => Promise.resolve([ // Dit moet in een promise omdat anders i18n nog niet initialized is. De promise wordt nml pas at runtime aangeroepen
                new FrontendFilterOption('0', i18n.t(`frontend-filters.orders.filters.has_assign_error.options.0`).toString()),
                new FrontendFilterOption('1', i18n.t(`frontend-filters.orders.filters.has_assign_error.options.1`).toString()),
                new FrontendFilterOption('2', i18n.t(`frontend-filters.orders.filters.has_assign_error.options.2`).toString()),
                new FrontendFilterOption('3', i18n.t(`frontend-filters.orders.filters.has_assign_error.options.3`).toString()),
            ])
        ),

        new FrontendFilter(
            'order_placed_from',
            new FrontendFilterInputDate({
                ...defaultFrontendFilterInputParams,
                max: (filterValues: FrontendFilterValues): string | undefined => filterValues['order_placed_to'] as string | undefined,
                clearValueLink: true,
            }),
            undefined,
            undefined,
            (filterValues: FrontendFilterValues): FrontendFilterValues => {
                if (filterValues['order_placed_from']) {
                    return filterValues
                }

                // When no value is set, calculate with max_age_days
                let newStartDate = DateHelper(filterValues['order_placed_to'] ?? new Date, 'YYYY-MM-DD')
                const daysToSub = (parseInt(filterValues['max_age_days'])) ? parseInt(filterValues['max_age_days']) : DEFAULT_MAX_AGE_DAYS;

                newStartDate.sub("day", daysToSub);

                filterValues['order_placed_from'] = newStartDate.getFormated('YYYY-MM-DD')
                return filterValues
            },
        ),

        new FrontendFilter(
            'order_placed_to',
            new FrontendFilterInputDate({
                ...defaultFrontendFilterInputParams,
                min: (filterValues: FrontendFilterValues): string | undefined => filterValues['order_placed_from'] as string | undefined,
                clearValueLink: true,
            }),
            undefined,
            undefined,
            (filterValues: FrontendFilterValues): FrontendFilterValues => {
                filterValues['order_placed_to'] = (!filterValues['order_placed_to'])
                    ? DateHelper(new Date()).getFormated('YYYY-MM-DD')
                    : filterValues['order_placed_to']

                return filterValues
            },
        ),

        new FrontendFilter(
            'max_age_days',
            new FrontendFilterInputNumber({
                ...defaultFrontendFilterInputParams,
                min: () => 0,
                max: () => 30,
                placeholder: (filterValues: FrontendFilterValues): string => {
                    return (filterValues['order_placed_from'])
                        ? 'n.v.t.'
                        : DEFAULT_MAX_AGE_DAYS.toString();
                },
                disabled: (filter: FrontendFilter, filterValues: FrontendFilterValues): boolean => {
                    if (defaultFrontendFilterInputParams.disabled(filter, filterValues)) {
                        return true
                    }

                    if (filterValues['order_placed_from']) {
                        Vue.set(filterValues, 'max_age_days', '');
                        return true;
                    }

                    if (filterValues['order_placed_from'] === '') {
                        Vue.set(filterValues, 'max_age_days', 30);
                    }

                    return false;
                },
            }),
        ),

        new FrontendFilter(
            'address_fulltext',
            new FrontendFilterInputText(defaultFrontendFilterInputParams),
        ),

        new FrontendFilter(
            'address_country',
            new FrontendFilterInputSelectOneWithSearch({...defaultFrontendFilterInputParams}),
            () => Promise.resolve(COUNTRY_OPTIONS()),
        ),

        new FrontendFilter(
            'has_pending_next_action_decision',
            new FrontendFilterInputBooleanSelect({...defaultFrontendFilterInputParams}),
        ),

        new FrontendFilter(
            'include_rerouted_replaced_dispatch_requests',
            new FrontendFilterInputBooleanSelect({...defaultFrontendFilterInputParams}),
            undefined,
            undefined,
            undefined,
            () => hasPermission('orders.dispatchrequests.include.reroutedreplaceddispatchrequests') && !hasPermission('canmanagealllocations')
        ),
    ];

    /** Columns **/

    columns = [
        new FrontendFilterResultColumn(
            'order_identifier',
            (resultRow) => resultRow.order_identifier,
            () => Promise.resolve(),
            'order_identifier'
        ),
        new FrontendFilterResultColumn(
            'placed_at',
            (resultRow) => resultRow.placed_at,
        ),
        new FrontendFilterResultColumn(
            'dispatch_date',
            (resultRow) => resultRow.current_dispatch_requests_with_paused[0]?.dispatch_date,
        ),
        new FrontendFilterResultColumn(
            'dispatch_type',
            (resultRow) => {
                let value = (Object.values(store.state.dispatchTypes ?? {}) as Record<string, any>[])
                    .find((dispatchType: Record<string, any>) => resultRow.current_dispatch_requests_with_paused[0]?.dispatch_type === dispatchType?.identifier)
                    ?.display_name ?? resultRow.current_dispatch_requests_with_paused[0]?.dispatch_type ?? ''

                value = value + ((resultRow.current_dispatch_requests_with_paused[0]?.auto_purchase) ? ' (automatisch)' : '')
                return value
            },
            store.getters.getDispatchTypes,
            'dispatch_type',
        ),
        new FrontendFilterResultColumn(
            'grand_total',
            (resultRow) => price(resultRow.grand_total),
        ),
        new FrontendFilterResultColumn(
            'payment_method',
            (resultRow): string => {
                let mappedMethods: string[] = resultRow.payments?.map((p: Record<string, any>) => {
                    return store.getters.paymentMethodOptions
                        ?.find((m: Record<string, any>) => m.id === p.payment_service_provider_account_method_id?.toString())?.display_name
                        ?? p.payment_service_provider_account_method_id
                })

                return mappedMethods?.filter((v) => v)?.join("<br/>")
            },
            () => store.getters.getPaymentMethodsInformation,
            'payment_method',
        ),
        new FrontendFilterResultColumn(
            'dispatcher_type',
            (resultRow) => {
                const dataTypes = store.state.locationDataTypes as Record<string,any>
                return dataTypes['locationTypes']
                    .find((type: Record<string, any>) => type.identifier === resultRow.current_dispatch_requests_with_paused[0]?.dispatcher_type)
                    ?.display_name
            },
            () => store.getters.getLocationDataTypes,
            'dispatcher_type',
        ),
        new FrontendFilterResultColumn(
            'dispatcher',
            (resultRow) => (store.state.allLocations
                ?.find((location: Record<string,any>) =>
                    location.identifier === resultRow.current_dispatch_requests_with_paused[0]?.dispatcher_identifier
                    && location.location_type?.identifier === resultRow.current_dispatch_requests_with_paused[0]?.dispatcher_type
                ) as unknown as Record<string,any>)
                ?.display_name,
            () => store.getters.getAllLocations,
            'dispatcher_identifier'
        ),
        new FrontendFilterResultColumn(
            'responsible_location',
            (resultRow) => (store.state.allLocations
                ?.find((location: Record<string,any>) =>
                    location.id === resultRow.current_dispatch_requests_with_paused[0]?.responsible_location_id
                ) as unknown as Record<string,any>)
                ?.display_name,
            () => store.getters.getAllLocations,
            'responsible_location_identifier',
        ),
        new FrontendFilterResultColumn(
            'dispatch_request_status',
            (resultRow) => dispatchRequestStatus(resultRow.current_dispatch_requests_with_paused[0]?.status, resultRow.current_dispatch_requests_with_paused[0]?.dispatch_type?.identifier),
            undefined,
            'dispatch_request_status',
        ),
        new FrontendFilterResultColumn(
            'validation_status',
            (resultRow) => FULFILMENT_OPTIONS()[resultRow.current_dispatch_requests_with_paused[0]?.validation_status]?.text ?? '',
            undefined,
            'validation_status',
        ),
        new FrontendFilterResultColumn(
            'carrier_method',
            (resultRow) => getCarrierMethodOptions()
                // @ts-ignore
                .find((option: FrontendFilterOption): FrontendFilterOption => option.value === resultRow.current_dispatch_requests_with_paused[0]?.carrier_method_id?.toString())
                // @ts-ignore
                ?.text ?? '',
            undefined,
            'carrier_method',
        ),
        new FrontendFilterResultColumn(
            'product_count',
            (resultRow) => _.sumBy(resultRow.sales_order_lines, (line: Record<string,any>) => line.quantity)?.toString(),
        ),
        new FrontendFilterResultColumn(
            'fulfilment_method',
            (resultRow): string => FULFILMENT_OPTIONS().find((option: FrontendFilterOption) => option.value === resultRow.fulfilment_method)?.text ?? '',
            undefined,
            'fulfilment_method',
        ),
        new FrontendFilterResultColumn(
            'current_fulfilment_method',
            (resultRow): string => FULFILMENT_OPTIONS().find((option: FrontendFilterOption) => option.value === resultRow.current_fulfilment_method)?.text ?? '',
            undefined,
            'current_fulfilment_method',
        ),
        new FrontendFilterResultColumn(
            'promised_delivery_date',
            (resultRow): string => resultRow.promised_delivery_date
        ),
        new FrontendFilterResultColumn(
            'sales_formula',
            (resultRow): string => resultRow.sales_formula_identifier,
            undefined,
            'sales_formula_identifier',
        ),
        new FrontendFilterResultColumn(
            'sales_channel',
            (resultRow): string => resultRow.sales_channel_identifier,
            undefined,
            'sales_channel_identifier',
        ),
        new FrontendFilterResultColumn(
            'customer_name',
            (resultRow): string => resultRow.addresses?.billing?.full_name,
        ),
        new FrontendFilterResultColumn(
            'customer_city',
            (resultRow): string => resultRow.addresses?.billing?.city,
        ),
        new FrontendFilterResultColumn(
            'customer_email',
            (resultRow) => resultRow.addresses?.billing?.email_address,
        ),
        new FrontendFilterResultColumn(
            'customer_country',
            (resultRow) => resultRow.addresses?.billing?.country,
        ),
        new FrontendFilterResultColumn(
            'customer_identifier',
            (resultRow) => resultRow.customer_identifier,
        ),
        new FrontendFilterResultColumn(
            'refund_status',
            (resultRow) => resultRow.refund_requests?.map((rrq: Record<string,any>) => i18n.t(`omt.gui.orders.refund-request-statuses.${rrq.status}`).toString()).join(', '),
        ),
        new FrontendFilterResultColumn(
            'dispatch_request_count',
            (resultRow) => resultRow.current_dispatch_requests_with_paused.length,
        ),
        new FrontendFilterResultColumn(
            'dispatch_request_batch',
            (resultRow) => resultRow.current_dispatch_requests_with_paused[0]?.dispatch_request_batch?.identifier ?? '',
        ),
        new FrontendFilterResultColumn(
            'is_dispatch_assign_retryable',
            (resultRow) => {
                const mapValueToFilterValue: Record<string,string> = {
                    undefined: "0",
                    null: "0",
                    true: "1",
                    false: "2",
                }

                return i18n.t(`omt.gui.orders.search.results.has-assign-error.${mapValueToFilterValue[resultRow.current_dispatch_requests_with_paused[0]?.is_dispatch_assign_retryable]}`)?.toString()
            },
        ),
        new FrontendFilterResultColumn(
            'has_comments',
            (resultRow) => i18n.tc(`frontend-filters.orders.columns.has_comments.result_text`, resultRow.comment_count, {count: resultRow.comment_count}).toString(),
            undefined,
            'has_comments',
        ),
        new FrontendFilterResultColumn(
            'has_pending_next_action_decision',
            (resultRow) => {
                const hasRelevantDrq = resultRow.current_dispatch_requests_with_paused.some((drq: any) => drq.requires_next_action_decision);
                const langKey = (hasRelevantDrq) ? 'yes' : 'no';
                return i18n.t(`omt.gui.general.${langKey}`).toString()
            },
        ),
    ];
}
